import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft, Eraser } from 'lucide-react';
import {
  WRITE_OFF_CAP_CENTS,
  type BulkWriteOffCommitRequest,
  type BulkWriteOffCommitResponse,
  type BulkWriteOffOrder,
  type BulkWriteOffPreviewResponse,
  type BulkWriteOffSide,
} from '@billflow/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { BulkWriteOffTable } from '@/components/BulkWriteOffTable';
import { BulkWriteOffFilters } from '@/components/BulkWriteOffFilters';
import { apiFetch } from '@/lib/api';
import { formatCents, todayISO } from '@/lib/utils';
import { startOfMonth } from '@/components/DateRangePresets';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PreviewResponse {
  data: BulkWriteOffPreviewResponse;
}
interface CommitResponse {
  data: BulkWriteOffCommitResponse;
}

interface SubmittedFilters {
  side: BulkWriteOffSide;
  from: string;
  to: string;
  maxCents: number;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const SIDE_NOUN: Record<BulkWriteOffSide, string> = {
  ar: 'invoice',
  ap: 'purchase order',
};

// Friendly labels for the skip reasons the commit endpoint can return
// (see server/src/lib/writeOff.ts + poWriteOff.ts + routes/writeOffs.ts).
const SKIP_REASON_LABEL: Record<string, string> = {
  NOT_FOUND: 'no longer found',
  BAD_STATUS: 'status changed since preview',
  NOTHING: 'nothing owed anymore',
  TOO_LARGE: 'balance exceeds the $50 cap',
  OVER_THRESHOLD: 'balance grew past the max',
};

function skipReasonLabel(code: string): string {
  return SKIP_REASON_LABEL[code] ?? code;
}

function dollarsToCents(str: string): number {
  return Math.round((parseFloat(str) || 0) * 100);
}

/** Clamp a dollars-string input into [1, WRITE_OFF_CAP_CENTS] cents, UI-side. */
function clampMaxCents(str: string): number {
  return Math.max(1, Math.min(WRITE_OFF_CAP_CENTS, dollarsToCents(str)));
}

function pluralize(count: number, noun: string): string {
  return count === 1 ? noun : `${noun}s`;
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function BulkWriteOffPage() {
  // Filter controls (live, edited by the admin before clicking Load).
  const [side, setSide] = useState<BulkWriteOffSide>('ar');
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(todayISO());
  const [maxDollars, setMaxDollars] = useState('50.00');

  // Filters actually submitted to the preview endpoint (only updated by Load).
  const [submitted, setSubmitted] = useState<SubmittedFilters | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [commitResult, setCommitResult] = useState<BulkWriteOffCommitResponse | null>(null);
  // Snapshot of the orders that were part of the last commit request, so the
  // written/skipped summary can show a number + party even after the preview
  // list refreshes (and those rows drop out of `orders`).
  const [requestedOrders, setRequestedOrders] = useState<BulkWriteOffOrder[]>([]);

  const maxCents = clampMaxCents(maxDollars);
  const datesValid = Boolean(from && to && from <= to);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['write-offs', 'bulk-preview', submitted],
    queryFn: () =>
      apiFetch<PreviewResponse>(
        `/api/write-offs/bulk/preview?side=${submitted!.side}&from=${submitted!.from}&to=${submitted!.to}&max_cents=${submitted!.maxCents}`,
      ),
    enabled: submitted !== null,
  });

  const preview = data?.data;
  const orders = useMemo(() => preview?.orders ?? [], [preview]);

  // Default every freshly-loaded row to checked.
  useEffect(() => {
    setSelectedIds(new Set(orders.map((o) => o.id)));
  }, [orders]);

  // Filters on screen have moved on from what's actually loaded.
  const dirty =
    submitted !== null &&
    (submitted.side !== side ||
      submitted.from !== from ||
      submitted.to !== to ||
      submitted.maxCents !== maxCents);

  function handleLoad() {
    setCommitResult(null);
    setSubmitted({ side, from, to, maxCents });
  }

  // Never let the shown per-order max exceed the $50 cap (the server clamps too,
  // but a silently-clamped input would mislead the admin about what was applied).
  function handleMaxDollarsChange(value: string) {
    if (value !== '' && dollarsToCents(value) > WRITE_OFF_CAP_CENTS) {
      setMaxDollars((WRITE_OFF_CAP_CENTS / 100).toFixed(2));
    } else {
      setMaxDollars(value);
    }
  }

  function toggleOne(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds((prev) =>
      prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.id)),
    );
  }

  const selectedOrders = useMemo(
    () => orders.filter((o) => selectedIds.has(o.id)),
    [orders, selectedIds],
  );
  const selectedCount = selectedOrders.length;
  const selectedTotal = selectedOrders.reduce((sum, o) => sum + o.remaining, 0);

  const requestedById = useMemo(
    () => new Map(requestedOrders.map((o) => [o.id, o])),
    [requestedOrders],
  );

  const commitMutation = useMutation({
    mutationFn: (body: BulkWriteOffCommitRequest) =>
      apiFetch<CommitResponse>('/api/write-offs/bulk', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      setCommitResult(res.data);
      setConfirmOpen(false);
      void refetch();
    },
  });

  function handleConfirmCommit() {
    if (!submitted) return;
    setRequestedOrders(selectedOrders);
    commitMutation.mutate({
      side: submitted.side,
      ids: selectedOrders.map((o) => o.id),
      max_cents: submitted.maxCents,
    });
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="rounded-md bg-slate-900 p-2">
          <Eraser className="h-5 w-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bulk Write-Off</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Clear many small outstanding balances at once (max {formatCents(WRITE_OFF_CAP_CENTS)}
            /order)
          </p>
        </div>
      </div>

      {/* Filters */}
      <BulkWriteOffFilters
        side={side}
        onSideChange={setSide}
        from={from}
        onFromChange={setFrom}
        to={to}
        onToChange={setTo}
        maxDollars={maxDollars}
        onMaxDollarsChange={handleMaxDollarsChange}
        onLoad={handleLoad}
        isFetching={isFetching}
        datesValid={datesValid}
        dirty={dirty}
      />

      {/* Post-commit summary */}
      {commitResult && (
        <Card className="mb-6 border-green-200 dark:border-green-900/40 bg-green-50/60 dark:bg-green-900/10">
          <CardContent className="pt-6 space-y-3">
            <p className="text-sm font-medium">
              Wrote off {commitResult.count_written} of{' '}
              {commitResult.count_written + commitResult.count_skipped} selected —{' '}
              {formatCents(commitResult.total_written)} total.
            </p>
            {commitResult.count_skipped > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-1.5">
                  {commitResult.count_skipped} skipped:
                </p>
                <ul className="text-sm space-y-1">
                  {commitResult.skipped.map((s) => {
                    const order = requestedById.get(s.id);
                    return (
                      <li key={s.id} className="flex items-center justify-between gap-3">
                        <span>{order ? `${order.number} — ${order.party}` : `#${s.id}`}</span>
                        <span className="text-muted-foreground">{skipReasonLabel(s.reason)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading / error (before any results are shown) */}
      {submitted === null && !commitResult && (
        <p className="text-sm text-muted-foreground py-10 text-center">
          Set your filters above and click Load to preview eligible{' '}
          {side === 'ar' ? 'invoices' : 'purchase orders'}.
        </p>
      )}
      {submitted !== null && isLoading && (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
      )}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load eligible orders'}
        </p>
      )}

      {/* Results */}
      {preview && !isLoading && (
        <>
          <BulkWriteOffTable
            side={submitted!.side}
            orders={orders}
            selectedIds={selectedIds}
            onToggleOne={toggleOne}
            onToggleAll={toggleAll}
          />

          {orders.length >= 1000 && (
            <p className="mt-2 text-xs text-muted-foreground">
              Showing the first 1000 eligible{' '}
              {submitted!.side === 'ar' ? 'invoices' : 'purchase orders'}. Write these off, then
              reload (or narrow the date range) to handle more.
            </p>
          )}

          {orders.length > 0 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {selectedCount} selected · {formatCents(selectedTotal)}
              </p>
              <Button
                variant="destructive"
                disabled={selectedCount === 0}
                onClick={() => setConfirmOpen(true)}
              >
                <Eraser className="h-4 w-4 mr-1.5" />
                Write off {selectedCount} selected ({formatCents(selectedTotal)})
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirmation dialog */}
      {confirmOpen && submitted && (
        <ConfirmDialog
          title="Confirm bulk write-off"
          confirmLabel="Write Off"
          confirmVariant="destructive"
          loading={commitMutation.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmCommit}
          message={
            <div className="space-y-2">
              <p>
                Write off <strong className="text-foreground">{selectedCount}</strong>{' '}
                {pluralize(selectedCount, SIDE_NOUN[submitted.side])} totaling{' '}
                <strong className="text-foreground">{formatCents(selectedTotal)}</strong>?
              </p>
              <p>
                Each forgiven balance is recorded as a non-cash Adjustment on that order, exactly
                like the single-order write-off. This bulk action cannot be undone automatically.
              </p>
              {commitMutation.isError && (
                <p className="text-destructive">
                  {commitMutation.error instanceof Error
                    ? commitMutation.error.message
                    : 'Write-off failed'}
                </p>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}
